import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from 'src/environments/environment';

/**
 * Keycloak adapter state is held at MODULE scope, not on the injectable.
 *
 * Angular can construct a service more than once across lazy-loaded module graphs, and
 * keycloak-js throws "A Keycloak instance can only be initialized once" if init() runs twice.
 * Instance fields are not a safe guard against that; a module-level promise is.
 *
 * Ported from the working React PoC at
 * E:/work/altus-keycloak/sso-keycloak/frontend-react/src/useKeycloak.ts
 */
let keycloak: Keycloak | null = null;
let initPromise: Promise<boolean> | null = null;

/**
 * Hostnames allowed to run SSO, checked against `window.location.hostname` at runtime.
 *
 * Why a runtime check at all: one build artifact is promoted from lab to production, so the
 * environment file swapped in at build time cannot know which deployment it ended up on.
 * Both environment.ts and environment.prod.ts point at the production Keycloak, so without
 * this check the lab deployment silently authenticates real users against the production
 * realm — and asking Keycloak to allow the lab redirect_uri would mean widening the
 * production client's allow-list to a lab machine.
 *
 * Deliberately an ALLOWLIST rather than a lab denylist. An unknown host (a new staging box,
 * a copied build, someone's VM) ends up with SSO OFF, which costs a button and nothing else.
 * A denylist would leave SSO ON for every host nobody remembered to add, pointing it at the
 * production realm — which is exactly the failure mode this is here to prevent.
 *
 * TEMPORARY, and the replacement is known: have the API serve its own SSO config. The backend
 * already holds Keycloak:Authority and Keycloak:ClientId in appsettings and is same-origin
 * with this app (environment.host === '.'), so it always knows which deployment it is. Once
 * that endpoint exists, delete this list — do not keep growing it.
 */
const SSO_ALLOWED_HOSTNAMES = [
  'app.altusentertainment.com',
  'localhost',
];

/** Compared on hostname, not host: the port is irrelevant and only invites mismatches. */
function isSsoHostAllowed(): boolean {
  return SSO_ALLOWED_HOSTNAMES.indexOf(window.location.hostname) !== -1;
}

@Injectable({ providedIn: 'root' })
export class KeycloakService {
  /**
   * Single source of truth for "is SSO available on this deployment".
   *
   * Both the login button and init() must read this same value. When they disagree you get a
   * visible button that does nothing, or no button on a deployment where SSO works.
   */
  get isEnabled(): boolean {
    return !!environment.keycloak?.enabled && isSsoHostAllowed();
  }

  /**
   * Idempotent. Safe to call from an APP_INITIALIZER and from anywhere else.
   * Resolves false when Keycloak is disabled for this environment or host.
   */
  init(): Promise<boolean> {
    if (!this.isEnabled) {
      return Promise.resolve(false);
    }

    if (initPromise) {
      return initPromise;
    }

    keycloak = new Keycloak({
      url: environment.keycloak.url,
      realm: environment.keycloak.realm,
      clientId: environment.keycloak.clientId,
    });

    // onAuthError carries the real OAuth error that init()'s rejection throws away.
    keycloak.onAuthError = (errorData: any) => {
      console.error('[Keycloak] onAuthError'
        + ' | error=' + (errorData && errorData.error)
        + ' | description=' + (errorData && errorData.error_description));
    };

    // These options mirror the working React PoC (frontend-react/src/useKeycloak.ts) apart from the
    // asset path and useNonce.
    //
    // An earlier comment here claimed that `checkLoginIframe: false`, and removing `onLoad` /
    // `silentCheckSsoRedirectUri`, each "broke init()". That was a misreading: init() was failing
    // for the same reason in every variant, so changing those options never made any difference.
    // The real cause is recorded below.
    initPromise = keycloak.init({
      onLoad: 'check-sso',
      pkceMethod: 'S256',

      // REQUIRED with this adapter/server pair — without it init() always fails.
      //
      // keycloak-js 23.0.7 validates the `nonce` claim on the access token AND the refresh token as
      // well as the ID token:
      //
      //   if (useNonce && ((tokenParsed && tokenParsed.nonce != storedNonce) || ...)) {
      //       clearToken(); promise.setError();   // rejects with a bare `undefined`
      //   }
      //
      // Keycloak 24+ only puts `nonce` in the ID token, per the OIDC spec. So `tokenParsed.nonce` is
      // undefined, the comparison against the stored nonce is true, and the adapter throws away a
      // perfectly good session: POST /token returns 200 with a full token set, then init() rejects
      // with `undefined` and leaves authenticated=false. That is the exact failure this codebase hit,
      // and it is why the symptom looked like "the token does not work" — the exchange downstream was
      // never even called.
      //
      // Safe for this flow, not merely expedient: nonce is OPTIONAL for the authorization code flow
      // in the OIDC spec (it is REQUIRED only for implicit and hybrid), and PKCE S256 above already
      // binds the code to this client. Nonce guards ID token replay, which code+PKCE does not expose.
      //
      // The cleaner fix is aligning keycloak-js with the server major version, which drops this check
      // entirely. Deferred deliberately — that is a dependency upgrade with its own blast radius
      // (v25 went ESM-only, v26 removed options), tracked separately.
      useNonce: false,

      // Angular serves assets under /assets. The React PoC used the web root because Vite
      // serves public/ there — this is the one place the ported pattern must differ.
      silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
    });

    // keycloak-js rejects with `undefined` in several paths, so log the adapter state alongside the
    // error — the state is usually more informative than the error itself.
    // Logged as one flat string, not an object: DevTools collapses objects to "Object", which makes
    // the output useless when someone copies it out of the console to report a problem.
    initPromise.catch(err => {
      let serialised: string;
      try {
        serialised = err === undefined ? 'undefined'
          : err === null ? 'null'
          : typeof err === 'string' ? err
          : JSON.stringify(err) || String(err);
      } catch {
        serialised = String(err);
      }
      console.error(
        '[Keycloak] init rejected'
        + ' | errorType=' + (typeof err)
        + ' | error=' + serialised
        + ' | authenticated=' + keycloak?.authenticated
        + ' | hasToken=' + !!keycloak?.token
        + ' | url=' + window.location.href
      );
    });

    return initPromise;
  }

  /** True only when a Keycloak session is active. False for legacy Nebular sessions. */
  get isAuthenticated(): boolean {
    return !!keycloak?.authenticated;
  }

  get token(): string | null {
    return keycloak?.token ?? null;
  }

  /** Redirects the browser to the Keycloak login page. Nothing after this call runs. */
  login(): Promise<void> {
    return keycloak ? keycloak.login() : Promise.resolve();
  }

  /** Ends the Keycloak session only. The legacy Nebular logout path is untouched. */
  logout(): Promise<void> {
    return keycloak
      ? keycloak.logout({ redirectUri: window.location.origin })
      : Promise.resolve();
  }

  /**
   * Refreshes the access token when it expires within minValiditySeconds.
   * Resolves false when there is no adapter or no refresh was needed/possible.
   */
  updateToken(minValiditySeconds = 30): Promise<boolean> {
    return keycloak
      ? keycloak.updateToken(minValiditySeconds)
      : Promise.resolve(false);
  }
}
